/**
=========================================================
* Material Dashboard 2 PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { ReactNode, forwardRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Custom styles for MDButton
import MDButtonRoot from 'src/components/md/MDButton/MDButtonRoot';

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController } from 'src/md-context';

export type ButtonIdentifier = string;

// Declaring props types for MDButton
interface Props {
  identifier?: ButtonIdentifier;
  color?:
    | 'white'
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'light'
    | 'dark'
    | 'default';
  variant?: 'text' | 'contained' | 'outlined' | 'gradient';
  size?: 'small' | 'medium' | 'large';
  circular?: boolean;
  iconOnly?: boolean;
  children?: ReactNode;
  [key: string]: any;
}

const MDButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      color = 'white',
      variant = 'contained',
      size = 'medium',
      circular = false,
      iconOnly = false,
      children,
      identifier,
      ...rest
    },
    ref,
  ) => {
    const clickableId = `${identifier ? identifier + '-' : ''}${uuidv4()}`;
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [buttonId] = useState(clickableId);

    return (
      <MDButtonRoot
        data-clickable-id={buttonId}
        {...rest}
        ref={ref}
        ownerState={{ color, variant, size, circular, iconOnly, darkMode }}
      >
        {children}
      </MDButtonRoot>
    );
  },
);

MDButton.displayName = 'MDButton';

export default MDButton;
